import { API_BASE } from '@/shared/services/api';
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { useAuth } from '@/shared/contexts/AuthContext';
import { toast } from 'sonner';
import { Package, Loader2, Eye, EyeOff } from 'lucide-react';

const LoginPage = ({ showDemoCredentials = false }) => {
  const { t } = useTranslation(['auth', 'common']);
  
  React.useEffect(() => {
    console.log('--- EZERP FRONTEND DIAGNOSTICS ---');
    console.log('[Version] 2026-03-17 23:22 - Phase 2 Lock + 401 Loop Fix');
    console.log('[API] Using Backend URL:', API_BASE || 'origin');
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/select-context';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      console.log('[Login] Attempting login with:', { email, role: 'unknown' });
      const response = await login(email, password);
      console.log('[Login] Login successful, response:', response);
      toast.success(t('auth:loginSuccess'));

      console.log('[Login] Navigating to:', from);
      navigate(from, { replace: true });
    } catch (error) {
      console.error('[Login] Login failed:', error);
      toast.error(error.message || t('auth:loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Demo credentials helper
  const fillDemo = (role) => {
    const creds = {
      admin: { email: 'admin@ezerp.com', password: 'admin123' },
      manager: { email: 'manager@ezerp.com', password: 'manager123' },
      operator: { email: 'operator@ezerp.com', password: 'operator123' },
    };
    setEmail(creds[role].email);
    setPassword(creds[role].password);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md bg-card border-border backdrop-blur">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-16 h-16 rounded-2xl bg-gradient-to-br from-accent to-accent/80 flex items-center justify-center shadow-lg shadow-accent/20">
            <Package className="h-8 w-8 text-accent-foreground" />
          </div>
          <CardTitle className="text-3xl font-bold text-foreground font-['Space_Grotesk'] italic leading-none">EzERP</CardTitle>
          <CardDescription className="text-muted-foreground text-[10px] font-bold tracking-widest uppercase mt-1">
            {t('auth:loginSubtitle')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-muted-foreground font-bold text-[10px] uppercase tracking-widest">{t('auth:email')}</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-muted/50 border-border text-foreground placeholder:text-muted-foreground h-12 rounded-xl focus-visible:ring-accent/50"
                data-testid="login-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-muted-foreground font-bold text-[10px] uppercase tracking-widest">{t('auth:password')}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="bg-muted/50 border-border text-foreground placeholder:text-muted-foreground pr-10 h-12 rounded-xl focus-visible:ring-accent/50"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-200 transition-colors"
                  data-testid="toggle-password-visibility"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              className="w-full bg-accent hover:bg-accent/90 text-accent-foreground h-12 rounded-xl font-bold shadow-lg shadow-accent/20 transition-all duration-300 transform active:scale-[0.98]"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('auth:signingIn')}
                </>
              ) : (
                t('auth:signIn')
              )}
            </Button>
          </form>

          {showDemoCredentials && (
            <div className="mt-8 pt-6 border-t border-border">
              <p className="text-[10px] text-muted-foreground mb-4 text-center font-bold tracking-widest uppercase">{t('auth:demoAccounts')}</p>
              <div className="flex gap-3 justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fillDemo('admin')}
                  className="text-xs border-border bg-muted/50 text-muted-foreground hover:bg-accent/10 hover:text-accent hover:border-accent/40 rounded-lg transition-all"
                >
                  {t('auth:admin')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fillDemo('manager')}
                  className="text-xs border-border bg-muted/50 text-muted-foreground hover:bg-accent/10 hover:text-accent hover:border-accent/40 rounded-lg transition-all"
                >
                  {t('auth:manager')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fillDemo('operator')}
                  className="text-xs border-border bg-muted/50 text-muted-foreground hover:bg-accent/10 hover:text-accent hover:border-accent/40 rounded-lg transition-all"
                >
                  {t('auth:operator')}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
